import { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, CheckCircle, XCircle, RefreshCw, Info, PlayCircle, PauseCircle } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  DRAFT: "Draft", PROPOSED: "Proposed", PARTIALLY_ACCEPTED: "Partially accepted",
  ALL_ACCEPTED: "All accepted", REJECTED: "Rejected", EXPIRED: "Expired",
  ADMIN_APPROVED: "Admin approved", ADMIN_REJECTED: "Admin rejected",
  PENDING_FULFILMENT: "Pending fulfilment", FULFILMENT_CONFIRMED: "Fulfilment confirmed",
  PENDING_SETTLEMENT: "Pending settlement", COMPLETED: "Completed",
  CANCELLED: "Cancelled", DISPUTED: "Disputed", FAILED: "Failed",
};

const TABS = [
  { value: "", label: "All" },
  { value: "PROPOSED", label: "Awaiting acceptance" },
  { value: "ALL_ACCEPTED", label: "Awaiting approval" },
  { value: "PENDING_FULFILMENT", label: "In fulfilment" },
  { value: "FULFILMENT_CONFIRMED", label: "Ready to authorise" },
  { value: "PENDING_SETTLEMENT", label: "Pending settlement" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function CycleGraph({ legs, participants }) {
  const labelOf = (id) => {
    const p = participants.find((x) => x.company_id === id);
    return p?.company_name || id.slice(0, 8);
  };
  return (
    <div className="cycle-graph" data-testid="admin-cycle-graph">
      {legs.map((l) => (
        <div key={l.id} className="leg-row">
          <span className="node-pill">{labelOf(l.seller_company_id)}</span>
          <ArrowRight size={16} />
          <span className="leg-value">₹{l.trade_value.toLocaleString("en-IN")}</span>
          <ArrowRight size={16} />
          <span className="node-pill">{labelOf(l.buyer_company_id)}</span>
          <span className="muted small" style={{ marginLeft: 8 }}>{l.listing_title}</span>
        </div>
      ))}
    </div>
  );
}

function CycleDetail({ id, onBack }) {
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");

  const load = async () => {
    try { const r = await client.get(`/admin/circular-trades/${id}`); setC(r.data); setError(""); }
    catch (err) { setError(extractError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!c) return <div className="loading-block">Loading…</div>;

  const doAction = async (action, body) => {
    setBusy(true); setError("");
    try {
      const r = await client.post(`/admin/circular-trades/${id}/${action}`, body || {});
      setC(r.data);
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="cycle-detail" data-testid="admin-cycle-detail">
      <button className="link-button" onClick={onBack}><ChevronLeft size={14} /> Back to cycles</button>
      <div className="detail-head">
        <h2>{c.reference_number}</h2>
        <span className={`status-pill status-${c.status.toLowerCase()}`} data-testid="admin-cycle-status">
          {STATUS_LABEL[c.status] || c.status}
        </span>
      </div>
      {error && <div className="form-error">{error}</div>}
      <section className="cycle-stats">
        <div><p className="eyebrow">TOTAL VALUE</p><strong>₹{c.total_trade_value.toLocaleString("en-IN")}</strong></div>
        <div><p className="eyebrow">FEE (10%)</p><strong>₹{c.total_facilitator_fee.toLocaleString("en-IN")}</strong></div>
        <div><p className="eyebrow">PARTICIPANTS</p><strong>{c.participant_count}</strong></div>
        <div><p className="eyebrow">TYPE</p><strong>{c.cycle_type}</strong></div>
      </section>
      <section className="cycle-section">
        <h3>Cycle graph</h3>
        <CycleGraph legs={c.legs} participants={c.participants} />
      </section>
      <section className="cycle-section">
        <h3>Participants</h3>
        <table className="admin-table">
          <thead><tr><th>#</th><th>Company</th><th>Value</th><th>Response</th><th>Timestamps</th></tr></thead>
          <tbody>
            {c.participants.map((p) => (
              <tr key={p.id}>
                <td>{p.position + 1}</td>
                <td>{p.company_name}</td>
                <td>₹{p.agreed_credit_value.toLocaleString("en-IN")}</td>
                <td>{p.acceptance_status}</td>
                <td className="muted small">{p.accepted_at || p.rejected_at || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {c.legs.some((l) => l.transaction) && (
        <section className="cycle-section">
          <h3>Transactions</h3>
          <table className="admin-table">
            <thead><tr><th>Leg</th><th>Seller</th><th>Buyer</th><th>Fulfilment</th><th>Settlement</th><th>Txn ID</th></tr></thead>
            <tbody>
              {c.legs.map((l) => (
                <tr key={l.id}>
                  <td>{l.sequence + 1}</td>
                  <td>{l.seller_name}</td>
                  <td>{l.buyer_name}</td>
                  <td>{l.transaction?.fulfilment_status || "—"}</td>
                  <td>{l.transaction?.settlement_status || "—"}</td>
                  <td className="muted small">{l.transaction?.id?.slice(0, 8) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {c.ledger_entries && c.ledger_entries.length > 0 && (
        <section className="cycle-section">
          <h3>Credit ledger ({c.ledger_entries.length} entries)</h3>
          <table className="admin-table">
            <thead><tr><th>Type</th><th>Company</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Reference</th></tr></thead>
            <tbody>
              {c.ledger_entries.map((e) => (
                <tr key={e.id}>
                  <td>{e.entry_type}</td>
                  <td className="muted small">{e.company_id.slice(0, 8)}</td>
                  <td>{e.debit_amount ? `₹${e.debit_amount.toLocaleString("en-IN")}` : "—"}</td>
                  <td>{e.credit_amount ? `₹${e.credit_amount.toLocaleString("en-IN")}` : "—"}</td>
                  <td>₹{e.balance_after.toLocaleString("en-IN")}</td>
                  <td className="muted small">{e.reference_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {c.disputes && c.disputes.length > 0 && (
        <section className="cycle-section">
          <h3>Disputes ({c.disputes.length})</h3>
          <ul className="muted small">
            {c.disputes.map((d) => (
              <li key={d.id}>Leg {d.leg_sequence + 1} · {d.dispute_type} · {d.status}</li>
            ))}
          </ul>
        </section>
      )}
      <section className="cycle-actions">
        {(c.status === "ALL_ACCEPTED" || c.status === "PARTIALLY_ACCEPTED" || c.status === "PROPOSED") && (
          <>
            <textarea placeholder="Admin notes (optional)" value={notes}
                       onChange={(e) => setNotes(e.target.value)} data-testid="admin-notes-input" />
            <button className="primary-button" data-testid="approve-cycle-btn"
                      disabled={busy}
                      onClick={() => doAction("approve", { admin_notes: notes || undefined })}>
              <CheckCircle size={14} /> Approve cycle
            </button>
            <div className="reject-inline">
              <input placeholder="Rejection reason (min 5 chars)" value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="admin-reject-reason" />
              <button className="danger-button" data-testid="reject-cycle-btn"
                        disabled={busy || reason.length < 5}
                        onClick={() => doAction("reject", { reason })}>
                <XCircle size={14} /> Reject
              </button>
            </div>
          </>
        )}
        {c.status === "PENDING_FULFILMENT" && (
          <button className="secondary-button" data-testid="refresh-fulfilment-btn" disabled={busy}
                    onClick={() => doAction("refresh-fulfilment", {})}>
            <RefreshCw size={14} /> Refresh fulfilment status
          </button>
        )}
        {c.status === "FULFILMENT_CONFIRMED" && (
          <button className="primary-button" data-testid="authorise-settlement-btn"
                    disabled={busy} onClick={() => doAction("authorise-settlement", {})}>
            <PlayCircle size={14} /> Authorise settlement (reserve credit)
          </button>
        )}
        {c.status === "PENDING_SETTLEMENT" && (
          <button className="primary-button" data-testid="settle-cycle-btn"
                    disabled={busy} onClick={() => doAction("settle", {})}>
            <CheckCircle size={14} /> Settle cycle
          </button>
        )}
        {!["COMPLETED", "CANCELLED", "REJECTED", "ADMIN_REJECTED", "EXPIRED", "FAILED"].includes(c.status) && (
          <div className="reject-inline">
            <input placeholder="Cancellation reason" value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="admin-cancel-reason" />
            <button className="danger-button" data-testid="cancel-cycle-btn"
                      disabled={busy || reason.length < 5}
                      onClick={() => doAction("cancel", { reason })}>
              <PauseCircle size={14} /> Cancel cycle
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default function AdminCircularTradesPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState({});

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await client.get("/admin/circular-trades", { params: tab ? { status: tab } : {} });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (err) { setError(extractError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  if (detailId) return <CycleDetail id={detailId} onBack={() => { setDetailId(null); load(); }} />;

  return (
    <div className="admin-circular-trades" data-testid="admin-circular-trades-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-cycle-tabs">
          {TABS.map((t) => (
            <button key={t.value || "all"} type="button"
                      className={tab === t.value ? "active" : ""}
                      data-testid={`admin-filter-${t.value || "all"}`}
                      onClick={() => setTab(t.value)}>
              {t.label}{t.value && counts[t.value] > 0 && <span className="count">{counts[t.value]}</span>}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        items.length === 0 ? (
          <div className="empty-state">
            <p><Info size={16} /> No circular trades yet.</p>
          </div>
        ) : (
          <table className="admin-table" data-testid="admin-circular-trades-table">
            <thead><tr><th>Ref</th><th>Status</th><th>Total value</th><th>Fee</th><th>Participants</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`admin-cycle-row-${c.reference_number}`}>
                  <td>{c.reference_number}</td>
                  <td><span className={`status-pill status-${c.status.toLowerCase()}`}>{STATUS_LABEL[c.status] || c.status}</span></td>
                  <td>₹{c.total_trade_value.toLocaleString("en-IN")}</td>
                  <td>₹{c.total_facilitator_fee.toLocaleString("en-IN")}</td>
                  <td>{c.participant_count}</td>
                  <td className="muted small">{c.created_at?.slice(0, 10)}</td>
                  <td><button className="link-button" data-testid={`admin-open-cycle-${c.reference_number}`}
                              onClick={() => setDetailId(c.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
